import {
	getHandlingStatusIcon,
	getInteractionName,
	hasNoCallbackTarget,
} from "@/common/helpers";
import { utcIsoDateOrUndefined } from "@/common/helpers/dateHelper";
import { Flex, GapSize } from "@/components/atoms/flex";
import { Divider, Text, mergeClasses } from "@fluentui/react-components";
import { InteractionWorkspaceProps } from "./types";
import { InteractionPopoverType, InteractionType } from "@/common/types";
import { useStyle } from "./style";
import { InteractionSource } from "@/components/molecules/interactionSource";
import { InteractionHistoryListItemDto } from "../history/types";
import { useCallback, useState } from "react";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { Call20Regular, Mail20Regular } from "@fluentui/react-icons";
import { useIntl } from "react-intl";
import { useHistoryActions, useHistoryHandling } from "@/hooks/history";
import { NotesButton } from "@/components/atoms/notes";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { IconSize } from "@/components/molecules/interactionComposedIcon/types";
import { getHistoryInteractionIcon } from "@/common/helpers/interactionIconHelper";
import { WebchatDefaultCustomerName } from "@/common/constants";
import { useEmailOutbound, useVoiceOutbound } from "@/hooks/outbound";
import { MessagingConversationSummaryButton } from "@/components/atoms/messagingConversationSummary";
import { useQuery } from "@tanstack/react-query";
import {
	checkCanGenerateSummary,
	CanGenerateConversationSummary,
	checkEnableCallSummary,
	checkEnableTextSummary,
} from "@/repository/queryFns";

export const HistoryInteractionBar = (props: InteractionWorkspaceProps) => {
	const { historyItem: historyItem } = props;

	const style = useStyle();
	const interactionBarCommonStyle = useInteractionBarCommonStyle();

	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);

	const intl = useIntl();

	const { hasAnyOutboundRightEndpoints, isOutboundAllowed } =
		useVoiceOutbound();
	const emailOutbound = useEmailOutbound();

	const { reopenEmailConversation, openOutboundWorkspace } =
		useHistoryActions();

	const { agentName, setHoveredInteractionType, setHoveredMediaId } =
		useHistoryHandling();

	const { data: canGenerateSummary } = useQuery<boolean>({
		queryKey: [CanGenerateConversationSummary],
		queryFn: (_: any) => checkCanGenerateSummary(),
		refetchOnMount: "always",
	});

	// The queue's summary master switch, read per interaction type: calls carry enableCallSummary on the
	// voice queue, every other type carries enableTextSummary on the messaging queue. History spans queues
	// the agent is not a member of, so the agent's own queue list cannot answer this. Voicemails offer no
	// summary button at all, so neither flag is fetched for them.
	const isCall = historyItem.type === InteractionType.Call;
	const isVoicemail = historyItem.type === InteractionType.Voicemail;

	const { data: enableCallSummary } = useQuery<boolean>({
		queryKey: ["enableCallSummary", historyItem.queueId],
		queryFn: () => checkEnableCallSummary(historyItem.queueId ?? ""),
		enabled: !!historyItem.queueId && !isVoicemail && isCall,
	});

	const { data: enableTextSummary } = useQuery<boolean>({
		queryKey: ["enableTextSummary", historyItem.queueId],
		queryFn: () => checkEnableTextSummary(historyItem.queueId ?? ""),
		enabled: !!historyItem.queueId && !isVoicemail && !isCall,
	});

	// Undefined while the query is still in flight, or when the item has no queue to ask about — both
	// mean "not known to be enabled", so the button stays disabled rather than flashing as available.
	// Compared against true rather than defaulted, so a 200 whose body is not a boolean (a gateway or
	// auth interstitial) reads as disabled instead of truthy-therefore-enabled.
	const summaryFeatureEnabled =
		(isCall ? enableCallSummary : enableTextSummary) === true;

	const getOperationName = (historyItem: InteractionHistoryListItemDto) => {
		if (historyItem.type == InteractionType.Voicemail) {
			return intl.formatMessage({ id: "History.Voicemail" });
		}
		let part1 = "";
		if (historyItem.isCallback) {
			part1 = intl.formatMessage({
				id: `Interaction.Callback`,
			});
		} else {
			let operationType = historyItem.isMissed
				? "Missed"
				: historyItem.isInboundInteraction
					? "Inbound"
					: "Outbound";
			part1 = intl.formatMessage({
				id: `History.${operationType}.${InteractionType[historyItem.type]}`,
			});
		}
		let part2TranslationKey = "";
		if (historyItem.isColdTransferred) {
			part2TranslationKey = "History.ColdTransfer";
		} else if (historyItem.isWarmTransferred) {
			part2TranslationKey = "History.WarmTransfer";
		}

		const historySuffix = intl.formatMessage({ id: "History.HistorySuffix" });

		if (part2TranslationKey) {
			let part2 = intl.formatMessage({ id: part2TranslationKey });

			return `${part1} - ${part2} (${historySuffix})`;
		}

		return `${part1} (${historySuffix})`;
	};

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) =>
				oldState === type ? null : type
			);
		},
		[setInteractionPopoverType]
	);

	return (
		<div>
			<div
				className={mergeClasses(
					interactionBarCommonStyle.interactionBar,
					style.interactionBar
				)}
			>
				<Flex
					vAlign="center"
					className={mergeClasses(interactionBarCommonStyle.interactionText)}
				>
					<Flex
						vAlign="center"
						className={mergeClasses(
							interactionBarCommonStyle.interactionIcon,
							interactionBarCommonStyle.interactionIconColor
						)}
					>
						{getHistoryInteractionIcon(historyItem, IconSize.Large)}
					</Flex>
					<Flex className={style.minWidthZero} gap={GapSize.ExtraLarge}>
						<InteractionSource
							mainInfo={getInteractionName(
								historyItem.type,
								historyItem.name,
								intl
							)}
							secondaryInfo={
								historyItem.type !== InteractionType.Generic &&
								historyItem.contactInfo != WebchatDefaultCustomerName
									? historyItem.contactInfo
									: ""
							}
							tertiaryInfo={
								historyItem.type == InteractionType.Generic
									? historyItem.subject
									: ""
							}
						/>
					</Flex>
					<Divider className={interactionBarCommonStyle.divider} vertical />
				</Flex>
				<div
					className={mergeClasses(
						interactionBarCommonStyle.interactionText,
						interactionBarCommonStyle.interactionTimerAndButtonsContainer
					)}
				>
					<Flex
						className={interactionBarCommonStyle.interactionActionContainer}
						gap={GapSize.Small}
						hAlign="space-between"
					>
						<Flex
							column
							className={mergeClasses(
								interactionBarCommonStyle.interactionAction,
								style.interactionDescription
							)}
						>
							<Text
								align="end"
								className={interactionBarCommonStyle.actionText}
								title={getOperationName(historyItem)}
								children={getOperationName(historyItem)}
							/>
							<Text
								align="end"
								className={interactionBarCommonStyle.actionText}
								title={historyItem?.queueName}
								children={historyItem?.queueName}
							/>
						</Flex>
						<Flex
							vAlign="center"
							hAlign="center"
							className={style.handlingStatus}
						>
							<span
								onMouseEnter={() => {
									setHoveredMediaId(historyItem.interactionId);
									setHoveredInteractionType(historyItem.type);
								}}
								title={
									historyItem.isHandled
										? agentName
										: intl.formatMessage({ id: "History.Unhandled" })
								}
							>
								{getHandlingStatusIcon(historyItem.isHandled)}
							</span>
						</Flex>
						{historyItem.handlingTimeFormatted && (
							<Flex vAlign="center" className={interactionBarCommonStyle.timer}>
								{historyItem.handlingTimeFormatted}
							</Flex>
						)}
					</Flex>
					{!isVoicemail && (
						<>
							<NotesButton
								interactionId={historyItem.interactionId}
								editMode={false}
								toggleInteractionPopoverOpening={
									toggleInteractionPopoverOpening
								}
								interactionPopoverType={interactionPopoverType}
							/>
							<MessagingConversationSummaryButton
								canGenerateSummary={canGenerateSummary === true}
								interactionId={historyItem.interactionId}
								interactionType={historyItem.type}
								interactionStartDate={utcIsoDateOrUndefined(historyItem.arrivalTime?.toString())}
								summaryFeatureEnabled={summaryFeatureEnabled}
							/>
						</>
					)}
					<Flex vAlign="center">
						{(historyItem.type == InteractionType.Call ||
							historyItem.type == InteractionType.Voicemail) &&
							!hasNoCallbackTarget(historyItem) && (
							<SquareToggleButton
								disabled={
									!hasAnyOutboundRightEndpoints || !isOutboundAllowed
								}
								icon={<Call20Regular />}
								tooltip={intl.formatMessage({
									id: "History.CallBack",
								})}
								onClick={() => openOutboundWorkspace(historyItem)}
							/>
						)}
						{historyItem.type == InteractionType.Email && (
							<SquareToggleButton
								disabled={!emailOutbound.hasAnyOutboundRightEndpoints}
								icon={<Mail20Regular />}
								tooltip={intl.formatMessage({
									id: "History.ReopenEmail.ReopenEmailConversation",
								})}
								onClick={() => reopenEmailConversation(historyItem)}
							/>
						)}
					</Flex>
				</div>
			</div>
		</div>
	);
};
